/**
 * ProgressBar - Themed progress indicator (health / mana / stamina / xp / boss)
 * with a header row (theme name + percentage) above a rounded fill track.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type ProgressBarTheme = 'health' | 'mana' | 'stamina' | 'experience' | 'boss' | 'custom';

export interface ProgressBarOptions extends BaseUIComponentOptions {
  /** Current value of the bar */
  currentValue?: number;
  /** Maximum value of the bar */
  maxValue?: number;
  /** Minimum value of the bar */
  minValue?: number;
  /** Width of the bar in pixels */
  width?: number;
  /** Height of the bar in pixels (overrides theme default) */
  height?: number;
  /** Color theme preset */
  theme?: ProgressBarTheme;
  /** Custom fill color (overrides theme) */
  fillColor?: string;
  /** Custom background color */
  backgroundColor?: string;
  /** Custom border color */
  borderColor?: string;
  /** Bar style variant (retained for API compatibility) */
  style?: 'flat' | 'gradient' | 'rounded' | 'glow';
  /** Animation duration in milliseconds */
  animationDuration?: number;
  /** Enable smooth animations */
  animate?: boolean;
  /** Text display mode for the value (right-side label) */
  textDisplay?: 'none' | 'value' | 'percentage' | 'both' | 'custom';
  /** Custom text to display */
  customText?: string;
  /** Text color */
  textColor?: string;
  /** Override for the left-side label (defaults to theme name). */
  label?: string;
  /** Direction of the bar fill (retained for API compatibility) */
  direction?: 'horizontal' | 'vertical';
  /** Fill direction for horizontal bars */
  fillDirection?: 'left-to-right' | 'right-to-left';
  /** Show background bar */
  showBackground?: boolean;
  /** Show border */
  showBorder?: boolean;
  /** Border width in pixels */
  borderWidth?: number;
  /** Corner radius for rounded style */
  borderRadius?: number;
}

export class ProgressBar extends BaseUIComponent<ProgressBarOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Progress Bar',
    category: 'hud',
    summary: 'Themed progress indicator with header (label + percentage) above a rounded fill track.',
    useCases: ['health', 'mana', 'stamina', 'experience', 'xp', 'boss hp', 'progress', 'cooldown', 'loading'],
    optionsType: 'ProgressBarOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/progress-bar.html',
      styles: '@engine/assets/ui/styles/progress-bar.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private fillElement: HTMLElement | null = null;
  private valueElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;
  private animationId: number | null = null;
  private currentDisplayValue: number = 0;

  protected override getAssetPaths() {
    return {
      templatePath: ProgressBar.metadata.assetPaths.template,
      stylesPath: ProgressBar.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<ProgressBarOptions> {
    return {
      position: 'top-left',
      visible: true,
      customClasses: [],
      customStyles: {},
      currentValue: 0,
      maxValue: 100,
      minValue: 0,
      width: 240,
      height: 0,
      theme: 'health',
      fillColor: '',
      backgroundColor: '',
      borderColor: '',
      style: 'rounded',
      animationDuration: 300,
      animate: false,
      textDisplay: 'percentage',
      customText: '',
      textColor: '',
      label: '',
      direction: 'horizontal',
      fillDirection: 'left-to-right',
      showBackground: true,
      showBorder: true,
      borderWidth: 0,
      borderRadius: 9999,
    };
  }

  protected override getInitialData() {
    return {
      theme: this.options.theme,
      label: this.getLabelText(),
      displayText: this.getDisplayText(this.options.currentValue),
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-progress-bar') as HTMLElement | null;
    this.fillElement = this.layout.querySelector('[data-progress-bar-fill]') as HTMLElement | null;
    this.valueElement = this.layout.querySelector('[data-progress-bar-value]') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-progress-bar-label]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.currentDisplayValue = this.options.currentValue;
    this.applyInitialStyles();
    this.updateDisplay();
  }

  private applyInitialStyles(): void {
    if (!this.rootElement) return;
    if (this.options.width > 0) {
      this.rootElement.style.width = `${this.options.width}px`;
    }
    if (this.options.height > 0) {
      this.rootElement.style.setProperty('--ui-progress-track-height', `${this.options.height}px`);
    }
    if (this.options.fillColor) {
      this.rootElement.style.setProperty('--ui-progress-fill', this.options.fillColor);
      this.rootElement.style.setProperty('--ui-progress-fill-bg', this.options.fillColor);
    }
  }

  /**
   * Set the current value of the progress bar
   */
  public setValue(value: number, animate: boolean = this.options.animate): void {
    const clampedValue = Math.max(this.options.minValue, Math.min(this.options.maxValue, value));
    this.options.currentValue = clampedValue;

    if (animate) {
      this.animateToValue(clampedValue);
    } else {
      this.currentDisplayValue = clampedValue;
      this.updateDisplay();
    }
  }

  /**
   * Set the maximum value of the progress bar
   */
  public setMaxValue(maxValue: number): void {
    this.options.maxValue = maxValue;
    // Ensure current value doesn't exceed new max
    if (this.options.currentValue > maxValue) {
      this.setValue(maxValue);
    } else {
      this.updateDisplay();
    }
  }

  /**
   * Get the current value
   */
  public getValue(): number {
    return this.options.currentValue;
  }

  /**
   * Get the current percentage (0-100)
   */
  public getPercentage(): number {
    return this.calculatePercentage();
  }

  /**
   * Set custom text to display in the right-side value slot. Also switches
   * the text display mode to 'custom' so the text is shown immediately.
   */
  public setCustomText(text: string): void {
    this.options.customText = text;
    this.options.textDisplay = 'custom';
    this.updateText();
  }

  /** Set the theme. */
  public setTheme(theme: ProgressBarTheme): void {
    if (!this.rootElement) {
      this.options.theme = theme;
      return;
    }
    this.rootElement.classList.remove(`theme-${this.options.theme}`);
    this.options.theme = theme;
    this.rootElement.classList.add(`theme-${theme}`);
    this.updateLabel();
    this.applyCustomFill();
  }

  /** Set a custom fill color (overrides theme fill via CSS variables). */
  public setFillColor(color: string): void {
    this.options.fillColor = color;
    this.applyCustomFill();
  }

  private applyCustomFill(): void {
    if (!this.rootElement) return;
    if (this.options.fillColor) {
      this.rootElement.style.setProperty('--ui-progress-fill', this.options.fillColor);
      this.rootElement.style.setProperty('--ui-progress-fill-bg', this.options.fillColor);
    } else {
      this.rootElement.style.removeProperty('--ui-progress-fill');
      this.rootElement.style.removeProperty('--ui-progress-fill-bg');
    }
  }

  private calculatePercentage(): number {
    const range = this.options.maxValue - this.options.minValue;
    if (range <= 0) return 0;
    return Math.max(0, Math.min(100, ((this.currentDisplayValue - this.options.minValue) / range) * 100));
  }

  private getDisplayText(value: number = this.currentDisplayValue): string {
    const range = this.options.maxValue - this.options.minValue;
    const pct = range <= 0 ? 0 : Math.max(0, Math.min(100, ((value - this.options.minValue) / range) * 100));
    switch (this.options.textDisplay) {
      case 'none':
        return '';
      case 'value':
        return `${Math.round(value)}/${this.options.maxValue}`;
      case 'percentage':
        return `${Math.round(pct)}%`;
      case 'both':
        return `${Math.round(value)}/${this.options.maxValue} (${Math.round(pct)}%)`;
      case 'custom':
        return this.options.customText;
      default:
        return '';
    }
  }

  private getLabelText(): string {
    if (this.options.label) return this.options.label;
    switch (this.options.theme) {
      case 'health': return 'Health';
      case 'mana': return 'Mana';
      case 'stamina': return 'Stamina';
      case 'experience': return 'Experience';
      case 'boss': return 'Boss';
      default: return '';
    }
  }

  private animateToValue(targetValue: number): void {
    if (this.animationId) cancelAnimationFrame(this.animationId);

    const startValue = this.currentDisplayValue;
    const difference = targetValue - startValue;
    const startTime = performance.now();

    const tick = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.options.animationDuration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      this.currentDisplayValue = startValue + difference * easeOut;
      this.updateDisplay();
      if (progress < 1) {
        this.animationId = requestAnimationFrame(tick);
      } else {
        this.animationId = null;
        this.currentDisplayValue = targetValue;
        this.updateDisplay();
      }
    };

    this.animationId = requestAnimationFrame(tick);
  }

  private updateDisplay(): void {
    this.updateFill();
    this.updateText();
    this.updateLabel();
  }

  private updateFill(): void {
    if (!this.fillElement) return;
    this.fillElement.style.width = `${this.calculatePercentage()}%`;
  }

  private updateText(): void {
    if (!this.valueElement) return;
    this.valueElement.textContent = this.getDisplayText();
  }

  private updateLabel(): void {
    if (!this.labelElement) return;
    this.labelElement.textContent = this.getLabelText();
  }

  protected override onDestroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.rootElement = null;
    this.fillElement = null;
    this.valueElement = null;
    this.labelElement = null;
  }
}